import React, {useRef, useState, useEffect} from 'react';
import styles from '../../styles/components/LocationDetails.module.scss';
import classnames from 'classnames';
import {stripHash, normalizeAnchorHref} from '../../utils/anchors';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import arrowPrev from '../../assets/images/prev_arrow.svg';
import arrowNext from '../../assets/images/next_arrow.svg';
import persons from '../../assets/images/persons.png';
import location from '../../assets/images/location.svg';

export default function LocationDetails({data}) {
    const wrapperRef = useRef(null);
    const onScreen = useIntersectionObserver(wrapperRef);
    const [expandedAccordions, setExpandedAccordions] = useState({});
    const sliderRefs = useRef({});
    const sliderPopupRef = useRef(null);
    const [popup, setPopup] = useState(null);
    const [popupRoomIndex, setPopupRoomIndex] = useState(null);

    const sliderSettings = {
        dots: true,
        infinite: false,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
    };

    const settingsPopup = {
        dots: false,
        infinite: true,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
    };

    const toggleAccordion = (roomIndex, accordionId) => {
        const key = `${roomIndex}-${accordionId}`;
        setExpandedAccordions(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const handlePrevClick = (roomIndex) => {
        sliderRefs.current[roomIndex]?.slickPrev();
    };

    const handleNextClick = (roomIndex) => {
        sliderRefs.current[roomIndex]?.slickNext();
    };

    const handlePopupPrevClick = () => {
        sliderPopupRef.current?.slickPrev();
    };

    const handlePopupNextClick = () => {
        sliderPopupRef.current?.slickNext();
    };

    // Get all images from all rooms
    const allImages = data.rooms.flatMap(room => room.images);

    useEffect(() => {
        if (popup !== null && popupRoomIndex !== null) {
            // Get current room images for popup
            const currentRoomImages = popupRoomIndex !== null ? data.rooms[popupRoomIndex].images : [];

            // Find the index of the image within the room
            const currentImageIndex = currentRoomImages.findIndex(
                img => img.id === popup.id && img.url === popup.url
            );

            if (currentImageIndex !== -1) {
                sliderPopupRef.current?.slickGoTo(currentImageIndex);
            }

            if (typeof window !== 'undefined' && window.document) {
                document.body.style.overflow = 'hidden';
            }
        } else {
            document.body.style.overflow = 'unset';
        }
    }, [popup, popupRoomIndex, data.rooms]);

    return (
        <div
            className={classnames(
                styles.locationDetailsWrapper,
                styles[data.style],
                onScreen?.isIntersecting ? styles.visible : ''
            )}
            id={stripHash(data.anchorId)}
            ref={wrapperRef}
        >
            <div className={styles.backButton}>
                <a href={data.backButtonURL}>{data.backButtonTitle}</a>
            </div>

            {data.address && (
                <div className={styles.headerWrapper}>
                    <h1 className={styles.title}>{data.title}</h1>
                    <p className={styles.address}>
                        <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(data.address)}`}
                           target="_blank" rel="noopener noreferrer">
                            <img src={location} alt=""/>
                            {data.address}
                        </a>
                    </p>
                </div>
            )}

            <div className={styles.roomsWrapper}>
                {data.rooms.map((room, roomIndex) => (
                    <div key={roomIndex} className={styles.roomCard}>
                        <div className={styles.roomContent}>
                            <div className={styles.galleryColumn}>
                                <div className={styles.sliderWrapper}>
                                    <div className={styles.sliderArrows}>
                                        <button
                                            className={styles.sliderArrow}
                                            onClick={() => handlePrevClick(roomIndex)}
                                            style={{visibility: Math.ceil(room.images.length / 3) <= 1 ? 'hidden' : 'visible'}}
                                        >
                                            <img src={arrowPrev} alt="Zurück"/>
                                        </button>
                                        <button
                                            className={styles.sliderArrow}
                                            onClick={() => handleNextClick(roomIndex)}
                                            style={{visibility: Math.ceil(room.images.length / 3) <= 1 ? 'hidden' : 'visible'}}
                                        >
                                            <img src={arrowNext} alt="Weiter"/>
                                        </button>
                                    </div>
                                    <Slider
                                        {...sliderSettings}
                                        ref={ref => {
                                            sliderRefs.current[roomIndex] = ref;
                                        }}
                                    >
                                        {(() => {
                                            const slides = [];
                                            const imagesPerSlide = 3;
                                            for (let i = 0; i < room.images.length; i += imagesPerSlide) {
                                                const slideImages = room.images.slice(i, i + imagesPerSlide);
                                                slides.push(
                                                    <div key={i} className={styles.slideWrapper}>
                                                        <a href="/#" className={styles.image} onClick={(e) => {
                                                            e.preventDefault();
                                                            setPopup(slideImages[0]);
                                                            setPopupRoomIndex(roomIndex);
                                                        }}>
                                                            {slideImages[0] && (
                                                                <img src={slideImages[0].url} alt={slideImages[0].alt}/>
                                                            )}
                                                        </a>
                                                        {slideImages[1] && (
                                                            <a href="/#" className={styles.image} onClick={(e) => {
                                                                e.preventDefault();
                                                                setPopup(slideImages[1]);
                                                                setPopupRoomIndex(roomIndex);
                                                            }}>
                                                                <img src={slideImages[1].url} alt={slideImages[1].alt}/>
                                                            </a>
                                                        )}
                                                        {slideImages[2] && (
                                                            <a href="/#" className={styles.image} onClick={(e) => {
                                                                e.preventDefault();
                                                                setPopup(slideImages[2]);
                                                                setPopupRoomIndex(roomIndex);
                                                            }}>
                                                                <img src={slideImages[2].url} alt={slideImages[2].alt}/>
                                                            </a>
                                                        )}
                                                    </div>
                                                );
                                            }
                                            return slides;
                                        })()}
                                    </Slider>
                                </div>
                            </div>

                            <div className={styles.detailsColumn}>
                                <h2 className={styles.roomTitle}>{room.title}</h2>
                                {room.persons && (
                                    <p className={styles.roomPersons}><img src={persons} alt="Personen"/>{room.persons}
                                    </p>
                                )}
                                <div
                                    className={styles.roomDescription}
                                    dangerouslySetInnerHTML={{__html: room.description}}
                                />

                                <div className={styles.accordionWrapper}>
                                    {room.accordion.map((accordionItem) => {
                                        const accordionKey = `${roomIndex}-${accordionItem.id}`;
                                        const isExpanded = expandedAccordions[accordionKey];

                                        return (
                                            <div key={accordionItem.id} className={styles.accordionItem}>
                                                <button
                                                    className={classnames(
                                                        styles.accordionTitle,
                                                        isExpanded ? styles.expanded : ''
                                                    )}
                                                    onClick={() => toggleAccordion(roomIndex, accordionItem.id)}
                                                    aria-expanded={isExpanded}
                                                >
                                                    {accordionItem.title}
                                                </button>
                                                <div
                                                    className={classnames(
                                                        styles.accordionContent,
                                                        isExpanded ? styles.expanded : ''
                                                    )}
                                                >
                                                    <div
                                                        className={styles.accordionText}
                                                        dangerouslySetInnerHTML={{
                                                            __html: accordionItem.description
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                <div
                                    className={styles.roomDescriptionPost}
                                    dangerouslySetInnerHTML={{__html: room.description2}}
                                />
                                {room.ctaURL && room.ctaTitle && (
                                    <div style={{display: "flex", justifyContent: "flex-end"}}>
                                        <a href={room.newTab ? room.ctaURL : normalizeAnchorHref(`#${room.ctaURL}`)}
                                           target={room.newTab ? '_blank' : '_self'}
                                           className={classnames(styles.button, styles.primary)}>
                                            {room.ctaTitle}
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>


            {popup !== null && (
                <div className={styles.popup}>
                    <div
                        className={styles.overlay}
                        onClick={() => {
                            setPopup(null);
                        }}
                    >
                        <div className={styles.popupNavButtons} style={{visibility: popupRoomIndex !== null && data.rooms[popupRoomIndex].images.length <= 1 ? 'hidden' : 'visible'}}>
                            <button
                                className={styles.popupNavButton}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handlePopupPrevClick();
                                }}
                                aria-label="Vorheriges Bild"
                            >
                                <img src={arrowPrev} alt=""/>
                            </button>
                            <button
                                className={styles.popupNavButton}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handlePopupNextClick();
                                }}
                                aria-label="Nächstes Bild"
                            >
                                <img src={arrowNext} alt=""/>
                            </button>
                        </div>
                        <button
                            className={styles.closeBtn}
                            onClick={() => {
                                setPopup(null);
                            }}
                        >
                            <span aria-hidden={"true"}>X</span>
                            <span className={styles.srOnly}>Popup schließen</span>
                        </button>
                    </div>
                    <div className={styles.content}>
                        <Slider ref={sliderPopupRef} {...settingsPopup}>
                            {popupRoomIndex !== null && data.rooms[popupRoomIndex].images.map((item, index) => {
                                return (
                                    <div className={styles.slideWrapper} key={index}>
                                        <img src={item.url} alt={item.alt}/>
                                    </div>
                                );
                            })}
                        </Slider>
                    </div>
                </div>
            )}
        </div>
    );
}